import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const colorThemes = {
  dark: {
    backgroundColor: 'rgba(52, 58, 64, 0.95)',
    color: 'white',
    hoverColor: '#1e90ff',
  },
  primary: {
    backgroundColor: '#0d6efd',
    color: 'white',
    hoverColor: '#1e90ff',
  },
  light: {
    backgroundColor: '#f8f9fa',
    color: '#212529',
    hoverColor: '#0d6efd',
  },
};

export default function Navbar({ bg = 'dark' }) {
  const [isOpen, setIsOpen] = useState(false);
  const theme = colorThemes[bg] || colorThemes.dark;
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const fontFamily = isRTL ? "'Tajawal', sans-serif" : 'inherit';

  const closeDrawer = () => setIsOpen(false);
  const toggleLanguage = () =>
    i18n.changeLanguage(i18n.language === 'ar' ? 'en' : 'ar');

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/icon?family=Material+Icons');
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700&display=swap');

        .navbar {
          background-color: ${theme.backgroundColor};
          color: ${theme.color};
          font-family: Arial, sans-serif;
          padding: 0.5rem 1rem;
          position: relative;
          z-index: 100;
        }

        .navbar-header {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          position: relative;
        }

        .logo-container {
          justify-self: start;
          background-color: white;
          padding: 5px;
          border-radius: 4px;
          display: inline-block;
        }

        .logo-container img {
          height: 40px;
          width: auto;
          user-select: none;
        }

        .lang-switch {
          justify-self: end;
          border: 1px solid ${theme.color};
          color: ${theme.color};
          background: transparent;
          padding: 4px 8px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: bold;
        }

        .menu-button {
          background-color: white;
          border: 5px solid dodgerblue;
          font-size: 1.3rem;
          color: ${theme.backgroundColor};
          cursor: pointer;
          border-radius: 50%;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 6px rgba(24, 82, 207, 0.2);
          justify-self: center;
          transition: transform 0.2s ease;
        }

        .menu-button:hover {
          transform: scale(1.05);
        }

        .nav-links {
          position: absolute;
          top: 100%;
          left: 0;
          right: 0;
          background-color: ${theme.backgroundColor};
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 1rem 0;
          transform: translateY(${isOpen ? '0' : '-200%'});
          transition: transform 0.3s ease-in-out;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.4);
          z-index: 200;
        }

        .nav-link {
          color: ${theme.color};
          text-decoration: none;
          font-size: 1.2rem;
          margin: 0.75rem 0;
          font-weight: bold;
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 0.5rem 1rem;
          border-radius: 6px;
          transition: background-color 0.3s, color 0.3s;
        }

        .nav-link:hover {
          background-color: rgba(30, 144, 255, 0.1);
          color: ${theme.hoverColor};
        }

        .material-icons {
          font-size: 20px;
        }

        .text-rtl {
          direction: rtl;
          unicode-bidi: bidi-override;
          text-align: right;
        }

        .backdrop {
          position: fixed;
          top: 0;
          left: 0;
          height: 100vh;
          width: 100vw;
          background-color: rgba(0, 0, 0, 0.3);
          opacity: ${isOpen ? 1 : 0};
          visibility: ${isOpen ? 'visible' : 'hidden'};
          transition: opacity 0.3s ease;
          z-index: 150;
        }
      `}</style>

      <nav className="navbar" role="navigation" aria-label="main navigation">
        <div className="navbar-header">
          <Link to="/" className="logo-container" onClick={closeDrawer}>
            <img src="image/Logo.png" alt="Logo" draggable="false" />
          </Link>

          <button
            className="menu-button"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? t('menu.close') : t('menu.open')}
            aria-expanded={isOpen}
          >
            {isOpen ? '✕' : '☰'}
          </button>

          <button className="lang-switch" onClick={toggleLanguage}>
            {i18n.language === 'ar' ? 'ENGLISH' : 'العربية'}
          </button>
        </div>

        <div className="nav-links" onClick={closeDrawer}>
          <Link to="/" className="nav-link" style={{ fontFamily }}>
            <span className="material-icons">home</span>
            <span className={isRTL ? 'text-rtl' : ''}>{t('nav.home')}</span>
          </Link>

          <Link to="/project" className="nav-link" style={{ fontFamily }}>
            <span className="material-icons">work_outline</span>
            <span className={isRTL ? 'text-rtl' : ''}>{t('nav.projects')}</span>
          </Link>

          <Link to="/service" className="nav-link" style={{ fontFamily }}>
            <span className="material-icons">miscellaneous_services</span>
            <span className={isRTL ? 'text-rtl' : ''}>{t('nav.services')}</span>
          </Link>

          <Link to="/about" className="nav-link" style={{ fontFamily }}>
            <span className="material-icons">info</span>
            <span className={isRTL ? 'text-rtl' : ''}>{t('nav.about')}</span>
          </Link>
        </div>

        {isOpen && (
          <div className="backdrop" onClick={closeDrawer} aria-hidden="true" />
        )}
      </nav>
    </>
  );
}
